import MuiDrawer from '@mui/material/Drawer';
import MuiAppBar, { AppBarProps as MuiAppBarProps } from '@mui/material/AppBar';
import { Box, CSSObject, IconButton, List, ListItemButton, ListItemIcon, ListItemText, styled, Theme, Typography } from '@mui/material';
import Button from '../components/button/Button';
import AccountIcon from '../icons/Account';

const drawerWidth = 270;

interface AppBarProps extends MuiAppBarProps {
  open?: boolean;
}

const openedMixin = (theme: Theme): CSSObject => ({
  width: drawerWidth,
  transition: theme.transitions.create('width', {
    easing: theme.transitions.easing.sharp,
    duration: theme.transitions.duration.enteringScreen,
  }),
  borderRight: 'none',
  overflowX: 'hidden',
});

const closedMixin = (theme: Theme): CSSObject => ({
  transition: theme.transitions.create('width', {
    easing: theme.transitions.easing.sharp,
    duration: theme.transitions.duration.leavingScreen,
  }),
  overflowX: 'hidden',
  borderRight: 'none',
  width: `calc(${theme.spacing(7)} + 1px)`,
  [theme.breakpoints.up('sm')]: {
    width: `calc(${theme.spacing(9)})`,
  },
});

export const DrawerHeader = styled('div')(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  padding: theme.spacing(0, 1),
  ...theme.mixins.toolbar,
}));

export const AppBar = styled(MuiAppBar, {
  shouldForwardProp: (prop) => prop !== 'open',
})<AppBarProps>(({ theme }) => ({
  zIndex: theme.zIndex.drawer - 1,
  borderRadius: 0,
  background: theme.palette.background.default,
  transition: theme.transitions.create(['width', 'margin'], {
    easing: theme.transitions.easing.sharp,
    duration: theme.transitions.duration.leavingScreen,
  }),
  variants: [
    {
      props: ({ open }) => open,
      style: {
        marginLeft: drawerWidth,
        transition: theme.transitions.create(['width', 'margin'], {
          easing: theme.transitions.easing.sharp,
          duration: theme.transitions.duration.enteringScreen,
        }),
      },
    },
  ],
}));

export const Drawer = styled(MuiDrawer, { shouldForwardProp: (prop) => prop !== 'open' })(
  ({ theme }) => ({
    width: drawerWidth,
    flexShrink: 0,
    whiteSpace: 'nowrap',
    boxSizing: 'border-box',
    '& .MuiDrawer-paper': {
      borderRadius: 0
    },
    variants: [
      {
        props: ({ open }) => open,
        style: {
          ...openedMixin(theme),
          '& .MuiDrawer-paper': openedMixin(theme),
        },
      },
      {
        props: ({ open }) => !open,
        style: {
          ...closedMixin(theme),
          '& .MuiDrawer-paper': closedMixin(theme),
        },
      },
    ],
  }),
);

export const HideButton = styled(Button)(({ theme }) => ({
    position: 'absolute',
    bottom: theme.spacing(1.8),
    left: theme.spacing(3.7),
    fontSize: theme.typography.body1.fontSize,
    color: theme.palette.text.primary,
    '&:hover': {
      backgroundColor: 'transparent',
      boxShadow: 'none',
    },
    '&:active': {
      backgroundColor: 'transparent',
      boxShadow: 'none',
    },
    '&:focus': {
      backgroundColor: 'transparent',
    },

    '& svg': {
      color: theme.palette.text.secondary,
    }
}));

export const OpenIconButton = styled(IconButton)(({ theme }) => ({
    position: 'absolute',
    bottom: theme.spacing(2),
    left: theme.spacing(3.2),
    color: theme.palette.text.secondary,
}));

export const MainBox = styled<any>(Box)(({ theme }) => ({ flexGrow: 1, minHeight: '100vh', background: theme.palette.background.default }));

export const AvatarButton = styled(IconButton)({
  '&:hover': {
    backgroundColor: 'transparent !important',
  },
});

interface StyledComponentProps {
  open?: boolean;
}

export const StyledListItemButton = styled(ListItemButton)<StyledComponentProps>(({ open }) => ({
  minHeight: 42,
  borderRadius: '6px',
  paddingTop: '5px',
  paddingBottom: '5px',
  '&.Mui-selected': {
    backgroundColor: '#EBEDF5',
  },
  '&:hover': {
    backgroundColor: '#EBEDF5',
  },
  justifyContent: open ? 'initial' : 'center',
}));

export const StyledListItemText = styled(ListItemText)<StyledComponentProps>(({theme, open }) => ({
    opacity: open ? 1 : 0,
    marginRight: open ? theme.spacing(1) : 0,
}));

export const StyledListItemIcon = styled(ListItemIcon)<StyledComponentProps>(({ theme, open }) => ({
  minWidth: 0,
  justifyContent: 'center',
  marginRight: open ? theme.spacing(2) : 0,
}));

export const StyledParrentList = styled(List)(() => ({
  '& > .MuiListItem-root': {
    paddingLeft: 0
  },
  '& > .MuiListItem-root .MuiListItemButton-root .MuiListItemText-root': {    
    paddingLeft: '24px',
  },
}));

export const muiDrawerStyles = { '& .MuiDrawer-paper': { borderRadius: 0, maxWidth: { sm: '320px' }, width: '100%' } };
export const muiDrawerBackdropStyles = { backgroundColor: 'rgba(52, 48, 48, 0.13)',backdropFilter: 'blur(4px)' };
export const appBarStyles = { display: { xs: 'block', sm: 'block', md: 'none' }, position: 'fixed' };
export const toolbarStyles = { display: 'flex', justifyContent: 'space-between', paddingX: { xs: 1.3, sm: 1.3 } };
export const drawerHeaderIconStyles = { padding: '18px', borderRadius: 0, marginRight: '4px' };
export const drawerHeaderStyles = { opacity: { xs: 0, sm: 0, md: 1 }, justifyContent: 'end' };
export const firstPageIconStyles = { fontSize: '24px !important' };
export const signOutIconStyles = { fontSize: '18px !important', fill: '#6C6F80' };
export const drawerHeaderOpenStyles = { justifyContent: 'start', paddingLeft: { xs: 0, md: '22px' } };
export const drawerHeaderCloseStyles = { justifyContent: 'center' };
export const mobileMenuWrapperStyles = { gap: 1, display: 'flex', alignItems: 'center' };
export const elevationScrollStyles = { boxShadow: '0px 3px 6px 0px rgba(29, 30, 38, 0.1)', background: 'rgba(255, 255, 255, 0.01) !important', backdropFilter: 'blur(7px)' };
export const pageNavListStyles = { paddingLeft: '12px', paddingRight: '12px', mt: '3px' };

export const LogoTitleTypography = styled(Typography)(({ theme }) => ({
  marginLeft: theme.spacing(1.3),
  fontSize: '21px',
  textTransform: 'uppercase',
  fontWeight: 600,
  lineHeight: '27px',
  color: theme.palette.text.primary,
}));

export const LogoCaptionTypography = styled(Typography)(({ theme }) => ({
  marginLeft: theme.spacing(1.3),
  display: 'block',
  fontSize: '10px',
  color: theme.palette.text.primary,
}));

export const AccountLogo = styled(AccountIcon)(() => ({
  width: '36px',
  height: '36px',
}));

export const logoGridContainerStyles = { height: '100%', alignItems: 'center', justifyContent: 'center' };
export const logoGridOpenStyles = { display: 'inline-flex' };
export const logoGridCloseStyles = {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    height: '100%',
    width: '100%',
  };

export const referrerLinkBoxStyles = { display: 'flex', alignItems: 'center', gap: 0.2 };
export const referrerLinkIconStyles = { fontSize: '14px' };
export const headerAvatarStyles = { width: '36px', height: '36px' };
export const headerPopoverStyles = {marginTop: '12px'};

export const headerPopoverListStyles = { width: '100%', paddingBottom: '6px', minWidth: 180, maxWidth: 360 };
export const headerListSubHeadStyles = { fontSize: '12px', lineHeight: '36px' };
export const headerListItemButtonStyles = { paddingTop: '6px', paddingBottom: '6px', marginTop: '6px', '&:first-of-type': { marginTop: 0 } };
export const headerListItemIconStyles = { minWidth: '22px' };
export const headerIconStyles = { fontSize: '14px', fill: "#6C6F80" };